import { cn } from "@cliently/ui";
import { getWeekDaysContaining, isSameCalendarDay, startOfDay } from "./program-calendar-utils";

type ProgramWeekCalendarProps = {
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  locale: string;
  datesWithSessions?: Set<string>;
};

const dateKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const ProgramWeekCalendar = ({
  selectedDate,
  onSelectDate,
  locale,
  datesWithSessions,
}: ProgramWeekCalendarProps) => {
  const today = startOfDay(new Date());
  const weekDays = getWeekDaysContaining(selectedDate);

  return (
    <div
      className="flex justify-between gap-1"
      role="tablist"
      aria-label="Semaine"
    >
      {weekDays.map((day) => {
        const selected = isSameCalendarDay(day, selectedDate);
        const isToday = isSameCalendarDay(day, today);
        const hasSession = datesWithSessions?.has(dateKey(day)) ?? false;
        const weekdayLabel = day
          .toLocaleDateString(locale, { weekday: "short" })
          .replace(/\.$/u, "")
          .slice(0, 3)
          .toUpperCase();

        return (
          <button
            key={dateKey(day)}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onSelectDate(startOfDay(day))}
            className={cn(
              "flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-lg px-1 py-2 text-center transition-colors",
              selected
                ? "border-foreground text-foreground border"
                : "text-muted-foreground hover:text-foreground border border-transparent",
              !selected && isToday && "text-foreground/80",
            )}
          >
            <span className="text-[10px] font-semibold tracking-wide">
              {weekdayLabel}
            </span>
            <span
              className={cn(
                "text-sm font-bold tabular-nums",
                selected ? "text-foreground" : "",
              )}
            >
              {day.getDate()}
            </span>
            {hasSession && !selected ? (
              <span
                className="bg-primary mt-0.5 h-1 w-1 rounded-full"
                aria-hidden
              />
            ) : (
              <span className="mt-0.5 h-1 w-1" aria-hidden />
            )}
          </button>
        );
      })}
    </div>
  );
};
